import type { ComponentType } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  type NavigateProps,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import { Layout } from './components/Layout';
import { DashboardPage } from './pages/DashboardPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AgentsPage } from './pages/AgentsPage';
import { DialerPage } from './pages/DialerPage';
import { ContactsPage } from './pages/ContactsPage';
import { SettingsPage } from './pages/SettingsPage';
import { TasksPage } from './pages/TasksPage';
import { BillingPage } from './pages/BillingPage';
import { StatusPage } from './pages/StatusPage';
import { VoicemailsPage } from './pages/VoicemailsPage';
import { RealtimeWrapper } from './components/RealtimeWrapper';
import { buildDialerRedirectPath } from './lib/routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: 3000, // Fallback polling every 3 seconds
      staleTime: 1000,
    },
  },
});

function SignInPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center flex flex-col items-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 text-xl font-bold text-white mb-4">
            C
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Welcome to CallHub</h1>
          <p className="mt-2 text-muted-foreground">Sign in to access your dashboard</p>
        </div>
        <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />
      </div>
    </div>
  );
}

type RedirectToDialerProps = {
  NavigateComponent?: ComponentType<NavigateProps>;
};

export function RedirectToDialer({ NavigateComponent = Navigate }: RedirectToDialerProps = {}) {
  const location = useLocation();
  return <NavigateComponent to={buildDialerRedirectPath(location.search)} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RealtimeWrapper>
        <BrowserRouter>
          <Routes>
            <Route
              path="/sign-in/*"
              element={
                <SignedOut>
                  <SignInPage />
                </SignedOut>
              }
            />

            {/* Protected Routes */}
            <Route
              element={
                <>
                  <SignedIn>
                    <Layout />
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/sign-in" replace />
                  </SignedOut>
                </>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/inbox" element={<RedirectToDialer />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/dialer" element={<DialerPage />} />
              <Route path="/voicemails" element={<VoicemailsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </RealtimeWrapper>
    </QueryClientProvider>
  );
}
